import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import ACTIONS from "../../../../redux/action.js";
import axios from "../../../../shared/axios/axiosProxy.js";
import i18n from "../../../../utils/i18n";
import classes from "./commonCompanyType.module.scss";

const ASP_FLOW_SWITCH_CHOICE = "asp";

/** Manual path: EV card → default tax companytype (EvTaxForm default). */
const mapFlowSelectionToCompanyType = (flowSelection) => {
  if (
    flowSelection === "reg_industry_select" ||
    flowSelection === "other" ||
    flowSelection === "company"
  ) {
    return "";
  }
  if (flowSelection === "entrepreneur") {
    return "singlesecondary";
  }
  return flowSelection;
};

const Radio = ({ checked }) => (
  <span
    className={`${classes.radio} ${checked ? classes.radioChecked : ""}`}
    aria-hidden="true"
  />
);

const CommonCompanyType = ({
  companyTypeFlowSelection: selection,
  onCompanyTypeChange,
}) => {
  const dispatch = useDispatch();
  const searchID = useSelector((state) => state.company_store.searchID);

  useEffect(() => {
    dispatch(
      ACTIONS.addCompanyInfo({
        id: "flowswitchchoice",
        data: ASP_FLOW_SWITCH_CHOICE,
        stateObject: "company_store",
      }),
    );
    dispatch(
      ACTIONS.addCompanyInfo({
        id: "isfakecherry",
        data: false,
        stateObject: "company_store",
      }),
    );

    if (!searchID || searchID === "undefined" || searchID === "null") {
      return;
    }

    axios
      .patch(
        `/api/search/${searchID}`,
        { flowswitchchoice: ASP_FLOW_SWITCH_CHOICE },
        { headers: { "Content-Type": "application/json" } },
      )
      .catch((error) => {
        console.error(
          "Error saving flowswitchchoice to search:",
          error.response?.data || error.message,
        );
      });
  }, [dispatch, searchID]);

  useEffect(() => {
    if (!selection) {
      return;
    }

    const companytype = mapFlowSelectionToCompanyType(selection);

    dispatch(
      ACTIONS.addCompanyInfo({
        id: "companytype",
        data: companytype,
        stateObject: "company_store",
      }),
    );

    if (!searchID || searchID === "undefined" || searchID === "null") {
      return;
    }

    axios
      .patch(
        `/api/search/${searchID}`,
        { companytype },
        { headers: { "Content-Type": "application/json" } },
      )
      .catch((error) => {
        console.error(
          "Error saving companytype to search:",
          error.response?.data || error.message,
        );
      });
  }, [dispatch, selection, searchID]);

  const isEntrepreneur = selection === "entrepreneur";
  const isKft = selection === "kft";
  const isBt = selection === "bt";
  const isNyrt = selection === "nyrt";
  const isCivil = selection === "civil";
  const isKkt = selection === "kkt";
  const isCondo = selection === "condo";
  const isOtherLeaf =
    isBt ||
    isNyrt ||
    isCivil ||
    isKkt ||
    isCondo ||
    selection === "zrt" ||
    selection === "citycouncil";
  const isOtherOpen = selection === "other" || isOtherLeaf;

  return (
    <div className={classes.root}>
      <div className={classes.scrollArea}>
        <div className={classes.content}>
          <div className={classes.mainColumn}>
            <header className={classes.header}>
              <h1 className={classes.title}>
                {i18n.t("searchflow.companytypetitle")}
              </h1>
              <p className={classes.subtitle}>
                {i18n.t("searchflow.companytypesubtitle")}
              </p>
            </header>

            <div className={classes.rightColumn}>
              <div className={classes.cardsContainer}>
                <button
                  type="button"
                  className={`${classes.card} ${isEntrepreneur ? classes.selectedRow : ""}`}
                  onClick={() => onCompanyTypeChange("entrepreneur")}
                  aria-pressed={isEntrepreneur}
                >
                  <span
                    className={classes.cardEmoji}
                    role="img"
                    aria-label={i18n.t("oneClick.entrepreneur")}
                  >
                    👨‍💼
                  </span>
                  <div className={classes.cardContent}>
                    <div className={classes.cardTitle}>{i18n.t("searchflow.companytypeevtitle")}</div>
                  </div>
                  <Radio checked={isEntrepreneur} />
                </button>

                <button
                  type="button"
                  className={`${classes.card} ${isKft ? classes.selectedRow : ""}`}
                  onClick={() => onCompanyTypeChange("kft")}
                  aria-pressed={isKft}
                >
                  <span
                    className={classes.cardEmoji}
                    role="img"
                    aria-label={i18n.t("searchflow.companytypekftlabel")}
                  >
                    💼
                  </span>
                  <div className={classes.cardContent}>
                    <div className={classes.cardTitle}>{i18n.t("searchflow.companytypekfttitle")}</div>
                  </div>
                  <Radio checked={isKft} />
                </button>

                <div
                  className={`${classes.card} ${classes.cardExpandable} ${isOtherOpen ? classes.cardExpandableExpanded : ""} ${isOtherOpen ? classes.selectedRow : ""}`}
                >
                  <button
                    type="button"
                    className={classes.cardHeader}
                    onClick={() => {
                      if (!isOtherOpen) {
                        onCompanyTypeChange("other");
                      }
                    }}
                    aria-pressed={isOtherOpen}
                    aria-expanded={isOtherOpen}
                  >
                    <span className={classes.cardEmoji} role="img" aria-label={i18n.t("searchflow.companytypeotherlabel")}>
                      💬
                    </span>
                    <div className={classes.cardContent}>
                      <div className={classes.cardTitle}>{i18n.t("industrylabels.other")}</div>
                      <div className={classes.cardSubtitle}>
                        {i18n.t("searchflow.companytypeothersubtitle")}
                      </div>
                    </div>
                    <Radio checked={isOtherOpen} />
                  </button>

                  <div className={classes.subOptions} aria-hidden={!isOtherOpen}>
                    <button
                      type="button"
                      className={`${classes.subOption} ${isBt ? classes.subOptionSelected : ""}`}
                      onClick={() => onCompanyTypeChange("bt")}
                      aria-pressed={isBt}
                      tabIndex={isOtherOpen ? 0 : -1}
                    >
                      <div className={classes.subOptionContent}>
                        <div className={classes.subOptionTitle}>
                          {i18n.t("companylabels.bt")}
                        </div>
                      </div>
                      <Radio checked={isBt} />
                    </button>

                    <button
                      type="button"
                      className={`${classes.subOption} ${isNyrt ? classes.subOptionSelected : ""}`}
                      onClick={() => onCompanyTypeChange("nyrt")}
                      aria-pressed={isNyrt}
                      tabIndex={isOtherOpen ? 0 : -1}
                    >
                      <div className={classes.subOptionContent}>
                        <div className={classes.subOptionTitle}>
                          {i18n.t("searchflow.companytypenyrttitle")}
                        </div>
                        <div className={classes.subOptionSubtitle}>
                          {i18n.t("searchflow.companytypenyrtsubtitle")}
                        </div>
                      </div>
                      <Radio checked={isNyrt} />
                    </button>

                    <button
                      type="button"
                      className={`${classes.subOption} ${isCivil ? classes.subOptionSelected : ""}`}
                      onClick={() => onCompanyTypeChange("civil")}
                      aria-pressed={isCivil}
                      tabIndex={isOtherOpen ? 0 : -1}
                    >
                      <div className={classes.subOptionContent}>
                        <div className={classes.subOptionTitle}>{i18n.t("providervafform.civil")}</div>
                        <div className={classes.subOptionSubtitle}>
                          {i18n.t("searchflow.companytypecivilsubtitle")}
                        </div>
                      </div>
                      <Radio checked={isCivil} />
                    </button>

                    <button
                      type="button"
                      className={`${classes.subOption} ${isKkt ? classes.subOptionSelected : ""}`}
                      onClick={() => onCompanyTypeChange("kkt")}
                      aria-pressed={isKkt}
                      tabIndex={isOtherOpen ? 0 : -1}
                    >
                      <div className={classes.subOptionContent}>
                        <div className={classes.subOptionTitle}>
                          {i18n.t("companylabels.kkt")}
                        </div>
                      </div>
                      <Radio checked={isKkt} />
                    </button>

                    <button
                      type="button"
                      className={`${classes.subOption} ${isCondo ? classes.subOptionSelected : ""}`}
                      onClick={() => onCompanyTypeChange("condo")}
                      aria-pressed={isCondo}
                      tabIndex={isOtherOpen ? 0 : -1}
                    >
                      <div className={classes.subOptionContent}>
                        <div className={classes.subOptionTitle}>{i18n.t("companylabels.condo")}</div>
                      </div>
                      <Radio checked={isCondo} />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CommonCompanyType;
